import React, { useState, useCallback, useRef, useEffect } from 'react';
import { observer } from 'mobx-react';
import { TextField, Select, Pagination, Icon } from '@ann/web-components';
import { useCommunicationsPage } from './hooks';
import { useActivitySearch } from '../../context/ActivitySearchContext';
import { CommunicationCard } from './components';
import { CHANNEL_TYPE_OPTIONS, STATUS_OPTIONS, SORT_OPTIONS } from '../../constants';
import {
  TabContainer,
  HeaderContainer,
  TitleSection,
  Title,
  ItemCount,
  FiltersContainer,
  SearchContainer,
  FilterDropdownWrapper,
  SelectionActionBar,
  SelectionInfo,
  SelectedCount,
  SelectAllButton,
  SelectionActions,
  ActionButton,
  CloseSelectionButton,
  ListContainer,
  EmptyState,
  EmptyIcon,
  EmptyTitle,
  EmptyDescription,
  PaginationWrapper,
  PaginationInfo,
  PaginationText,
  LoadingContainer,
  SkeletonCard,
} from './CommunicationsTab.styled';

const CommunicationsTab = observer(() => {
  const {
    communications,
    meta,
    loading,
    error,
    filters,
    selectedIds,
    selectAll,
    handleSearchChange,
    handleChannelTypeChange,
    handleStatusChange,
    handleSortChange,
    toggleSelect,
    toggleSelectAll,
    clearSelection,
    handlePageChange,
    handleItemsPerPageChange,
    handleRefresh,
    handleMarkAsRead,
    handleAssign,
    handleClose,
  } = useCommunicationsPage();

  const { searchValue: headerSearchValue, setSearchValue: setHeaderSearchValue } = useActivitySearch();
  const [actionLoading, setActionLoading] = useState(null);
  const searchTimeoutRef = useRef(null);

  // Sync header search (context) -> Communications tab filter (debounced)
  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      handleSearchChange(headerSearchValue);
    }, 500);
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [headerSearchValue, handleSearchChange]);

  // Sync tab filter (e.g. clear) -> header search
  useEffect(() => {
    if (filters.search !== headerSearchValue) {
      setHeaderSearchValue(filters.search);
    }
  }, [filters.search]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle card click
  const handleCardClick = useCallback((communication) => {
    console.log('Communication clicked:', communication);
    // TODO: Open communication detail view
  }, []);

  // Handle menu click
  const handleMenuClick = useCallback((communication, event) => {
    console.log('Menu clicked:', communication);
    // TODO: Show context menu
  }, []);

  // Handle action buttons
  const handleMarkAsReadClick = useCallback(async () => {
    if (selectedIds.size === 0) return;
    setActionLoading('markAsRead');
    try {
      await handleMarkAsRead(Array.from(selectedIds));
    } finally {
      setActionLoading(null);
    }
  }, [selectedIds, handleMarkAsRead]);

  const handleAssignClick = useCallback(async () => {
    if (selectedIds.size === 0) return;
    setActionLoading('assign');
    try {
      await handleAssign(Array.from(selectedIds));
    } finally {
      setActionLoading(null);
    }
  }, [selectedIds, handleAssign]);

  const handleCloseClick = useCallback(async () => {
    if (selectedIds.size === 0) return;
    setActionLoading('close');
    try {
      await handleClose(Array.from(selectedIds));
    } finally {
      setActionLoading(null);
    }
  }, [selectedIds, handleClose]);

  const hasSelection = selectedIds.size > 0;

  // Render loading skeleton
  if (loading && communications.length === 0) {
    return (
      <TabContainer>
        <HeaderContainer>
          <TitleSection>
            <Title>Communications</Title>
          </TitleSection>
        </HeaderContainer>
        <LoadingContainer>
          {[1, 2, 3, 4, 5].map((i) => (
            <SkeletonCard key={i} />
          ))}
        </LoadingContainer>
      </TabContainer>
    );
  }

  // Render error state
  if (error && communications.length === 0) {
    return (
      <TabContainer>
        <HeaderContainer>
          <TitleSection>
            <Title>Communications</Title>
          </TitleSection>
        </HeaderContainer>
        <EmptyState>
          <EmptyIcon>
            <Icon name="error" width="48px" height="48px" />
          </EmptyIcon>
          <EmptyTitle>Failed to load communications</EmptyTitle>
          <EmptyDescription>
            {error.message || 'An error occurred. Please try again.'}
          </EmptyDescription>
        </EmptyState>
      </TabContainer>
    );
  }

  return (
    <TabContainer>
      {/* Header */}
      <HeaderContainer>
        <TitleSection>
          <Title>Communications</Title>
          <ItemCount>{meta.total_items} items</ItemCount>
        </TitleSection>
      </HeaderContainer>

      {/* Filters */}
      <FiltersContainer>
        <SearchContainer>
          <TextField
            iconLeft="search"
            placeholder="Search communications..."
            value={headerSearchValue}
            onChange={setHeaderSearchValue}
            size="small"
            disabled={loading}
          />
        </SearchContainer>
        <FilterDropdownWrapper>
          <Select
            placeholder="All Channels"
            value={filters.channelType}
            onChange={(selected) => handleChannelTypeChange(selected?.value || selected)}
            size="small"
            width="auto"
            minWidth="130px"
            disabled={loading}
          >
            <Select.Menu>
              {CHANNEL_TYPE_OPTIONS.map((option) => (
                <Select.Item key={option.value} value={option.value} label={option.label}>
                  {option.label}
                </Select.Item>
              ))}
            </Select.Menu>
          </Select>
        </FilterDropdownWrapper>
        <FilterDropdownWrapper>
          <Select
            placeholder="All Status"
            value={filters.status || 'all'}
            onChange={(selected) => handleStatusChange?.(selected?.value || selected)}
            size="small"
            width="auto"
            minWidth="130px"
            disabled={loading}
          >
            <Select.Menu>
              {STATUS_OPTIONS.map((option) => (
                <Select.Item key={option.value} value={option.value} label={option.label}>
                  {option.label}
                </Select.Item>
              ))}
            </Select.Menu>
          </Select>
        </FilterDropdownWrapper>
        <FilterDropdownWrapper>
          <Select
            placeholder="Most Recent"
            value={filters.sort || 'last_update'}
            onChange={(selected) => handleSortChange?.(selected?.value || selected)}
            size="small"
            width="auto"
            minWidth="130px"
            disabled={loading}
          >
            <Select.Menu>
              {SORT_OPTIONS.map((option) => (
                <Select.Item key={option.id} value={option.id} label={option.label}>
                  {option.label}
                </Select.Item>
              ))}
            </Select.Menu>
          </Select>
        </FilterDropdownWrapper>
      </FiltersContainer>

      {/* Selection Action Bar */}
      {hasSelection && (
        <SelectionActionBar>
          <SelectionInfo>
            <SelectedCount>{selectedIds.size} selected</SelectedCount>
            <SelectAllButton onClick={toggleSelectAll}>
              {selectAll ? 'Deselect All' : 'Select All'}
            </SelectAllButton>
          </SelectionInfo>
          <SelectionActions>
            <ActionButton
              onClick={handleMarkAsReadClick}
              disabled={actionLoading !== null}
            >
              {actionLoading === 'markAsRead' ? 'Loading...' : 'Mark as Read'}
            </ActionButton>
            <ActionButton
              onClick={handleAssignClick}
              disabled={actionLoading !== null}
            >
              {actionLoading === 'assign' ? 'Loading...' : 'Assign'}
            </ActionButton>
            <ActionButton
              onClick={handleCloseClick}
              disabled={actionLoading !== null}
            >
              {actionLoading === 'close' ? 'Loading...' : 'Close'}
            </ActionButton>
            <CloseSelectionButton onClick={clearSelection} title="Clear selection">
              <Icon name="close" width="18px" height="18px" />
            </CloseSelectionButton>
          </SelectionActions>
        </SelectionActionBar>
      )}

      {/* List */}
      {communications.length === 0 ? (
        <EmptyState>
          <EmptyIcon>
            <Icon name="forum" width="48px" height="48px" />
          </EmptyIcon>
          <EmptyTitle>No communications yet</EmptyTitle>
          <EmptyDescription>
            Emails and SMS conversations will appear here.
          </EmptyDescription>
        </EmptyState>
      ) : (
        <>
          {/* Communication Cards */}
          <ListContainer>
            {communications.map((communication) => (
              <CommunicationCard
                key={communication.id}
                communication={communication}
                isSelected={selectedIds.has(communication.id)}
                onSelect={toggleSelect}
                onClick={handleCardClick}
                onMenuClick={handleMenuClick}
              />
            ))}
          </ListContainer>

          {/* Pagination */}
          <PaginationWrapper>
            <PaginationInfo>
              <PaginationText>
                Showing {(meta.current_page - 1) * meta.per_page + 1} to{' '}
                {Math.min(meta.current_page * meta.per_page, meta.total_items)} of{' '}
                {meta.total_items} communications
              </PaginationText>
              <Pagination
                currentPage={meta.current_page}
                itemsPerPage={meta.per_page}
                onItemsPerPageChange={handleItemsPerPageChange}
                onPageChange={handlePageChange}
                totalItems={meta.total_items}
                totalPages={meta.total_pages}
                disabled={loading}
              />
            </PaginationInfo>
          </PaginationWrapper>
        </>
      )}
    </TabContainer>
  );
});

export default CommunicationsTab;
